
import { Component } from './component.js';
import { ThemeEvents } from './events.js';

const DEFAULT_DOG_WEIGHT = 20;

/** @param {any} dog */
function isOldDefaultDog(dog) {
  if (!dog || dog.userCreated === true) return false;
  const name = String(dog.name ?? '').trim();
  const weight = normalizeDogWeight(dog.weight);
  return !name && weight === DEFAULT_DOG_WEIGHT;
}

/**
 * @param {any} rawDogs
 * @returns {any[]}
 */
/**
 * Collision-proof dog id. Rapid "Add Another Dog" taps can land in the same
 * millisecond, so plain Date.now() handed two dogs the same id — every
 * id-keyed write then hit the first dog, the ATC built two identical cart
 * lines, Shopify merged them, and the add flow never reconciled (the
 * "4 pups + appliance freezes" bug). Bump past the max existing id instead.
 */
function newPackDogId(dogs) {
  let id = Date.now();
  for (const dog of Array.isArray(dogs) ? dogs : []) {
    const existing = Number(dog && dog.id);
    if (Number.isFinite(existing) && existing >= id) id = existing + 1;
  }
  return id;
}

function normalizeStoredDogs(rawDogs) {
  if (!Array.isArray(rawDogs)) return [];
  const dogs = rawDogs.filter((dog) => dog !== null && !isOldDefaultDog(dog));
  // Repair duplicate ids persisted by the old Date.now() generator:
  // keep the first occurrence, re-key the rest.
  const seen = new Set();
  for (const dog of dogs) {
    if (!dog || dog.id == null) continue;
    if (seen.has(String(dog.id))) dog.id = newPackDogId(dogs);
    seen.add(String(dog.id));
  }
  return dogs;
}

function normalizeDogWeight(weight) {
  const n = parseInt(String(weight ?? '').trim(), 10);
  return !Number.isNaN(n) && n > 0 ? n : DEFAULT_DOG_WEIGHT;
}

/** Clamp pack-builder weight to the same range as the calculator (4–200 lb). */
function clampPackDogWeight(weight) {
  const n = parseInt(String(weight ?? '').trim(), 10);
  if (Number.isNaN(n) || n <= 0) return DEFAULT_DOG_WEIGHT;
  return Math.min(200, Math.max(4, n));
}

/** @param {number} n */
function isPackDogWeightInRange(n) {
  return typeof n === 'number' && !Number.isNaN(n) && n >= 4 && n <= 200;
}

/**
 * Prefer `artie_active_pack_dog_id` over `artie-dogs-active` so UI matches the pill the shopper chose
 * even when the numeric index desyncs after `artie_pets_store` → `artie-dogs` rebuild.
 * @param {any[]} dogs
 * @returns {number}
 */
function activePackIndexForDogs(dogs) {
  if (!Array.isArray(dogs) || !dogs.length) return 0;
  try {
    const aid = localStorage.getItem('artie_active_pack_dog_id');
    if (aid != null && String(aid).trim() !== '') {
      const want = String(aid).trim();
      const at = dogs.findIndex((d) => d && String(d.id) === want);
      if (at >= 0) return at;
    }
  } catch (e) {}
  const savedStr = localStorage.getItem('artie-dogs-active');
  let idx = savedStr != null && savedStr.trim() !== '' ? parseInt(savedStr, 10) : 0;
  if (Number.isNaN(idx) || idx < 0 || idx >= dogs.length) idx = 0;
  return idx;
}

class PackBuilder extends Component {
  constructor() {
    super();
    this.dogs = [];
    this.activeIndex = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    
    // Load from localStorage or initialize
    const savedDogs = localStorage.getItem('artie-dogs');
    if (savedDogs) {
      try {
        const parsedDogs = JSON.parse(savedDogs);
        this.dogs = normalizeStoredDogs(parsedDogs);
        let storedIdsHadDupes = false;
        if (Array.isArray(parsedDogs)) {
          const rawIds = parsedDogs.filter((d) => d && d.id != null).map((d) => String(d.id));
          storedIdsHadDupes = new Set(rawIds).size !== rawIds.length;
        }
        if (Array.isArray(parsedDogs) && (parsedDogs.length !== this.dogs.length || storedIdsHadDupes)) {
          localStorage.setItem('artie-dogs', JSON.stringify(this.dogs));
          if (!this.dogs.length) localStorage.removeItem('artie_active_pack_dog_id');
        }
        this.activeIndex = activePackIndexForDogs(this.dogs);
        try {
          localStorage.setItem('artie-dogs-active', String(this.activeIndex));
        } catch (eIdx) {}
      } catch (e) {
        this.initDefaultDogs();
      }
    } else {
      this.initDefaultDogs();
    }

    // Setting-gated: seed the first dog row on boot so the panel always shows
    // name/weight fields and the add button can read "Add Another Dog".
    // Unlike addDog() this never focuses the input (no keyboard popup on load).
    let seededFirstDog = false;
    if (!this.dogs.length && this.dataset.autoFirstDog === 'true') {
      this.dogs.push({ id: newPackDogId(this.dogs), name: '', weight: DEFAULT_DOG_WEIGHT, userCreated: true });
      this.activeIndex = 0;
      this.saveState();
      seededFirstDog = true;
    }

    const weightsWereNormalized = this.ensureValidDogWeights();
    if (weightsWereNormalized) {
      localStorage.setItem('artie-dogs', JSON.stringify(this.dogs));
      this.activeIndex = activePackIndexForDogs(this.dogs);
      localStorage.setItem('artie-dogs-active', this.activeIndex.toString());
    }

    this.render();

    const bootActive = this.dogs[this.activeIndex];
    if (bootActive && bootActive.id != null) {
      localStorage.setItem('artie_active_pack_dog_id', String(bootActive.id));
    }
    document.dispatchEvent(new CustomEvent('artie:active-dog-changed', { bubbles: true }));
    if ((weightsWereNormalized || seededFirstDog) && bootActive && bootActive.id != null) {
      // The seeded dog must complete the same weight handshake addDog() does,
      // or artie-calc never resolves its meal plan and add-to-cart drops it.
      this.dispatchDogWeightQuizSync(bootActive.id, bootActive.weight, true);
    }
    
    // Save original text for global placeholders
    this.initializeGlobalPlaceholders();

    // Re-initialize placeholders when variant changes (since content is replaced)
    document.addEventListener(ThemeEvents.variantUpdate, () => {
      // Delay slightly to ensure DOM is updated
      setTimeout(() => {
        this.initializeGlobalPlaceholders();
        this.updateDynamicText();
      }, 50);
    });

    this._onCustomerPetsHydrated = () => {
      this.reloadDogsFromLocalStorage();
    };
    document.addEventListener('artie:customer-pets-hydrated', this._onCustomerPetsHydrated);

    this._onPackDogsFromStore = (ev) => {
      const skip = !!(ev && ev.detail && ev.detail.skipDogWeightQuizSync);
      this.reloadDogsFromLocalStorage(skip);
    };
    document.addEventListener('artie:pack-dogs-from-store', this._onPackDogsFromStore);
  }

  disconnectedCallback() {
    if (this._onCustomerPetsHydrated) {
      document.removeEventListener('artie:customer-pets-hydrated', this._onCustomerPetsHydrated);
    }
    if (this._onPackDogsFromStore) {
      document.removeEventListener('artie:pack-dogs-from-store', this._onPackDogsFromStore);
    }
    super.disconnectedCallback();
  }

  /**
   * After `artie-calc` updates `artie-dogs`, resync the UI.
   * @param {boolean} [skipDogWeightQuizSync] - When true (reload from artie-calc after persist), do not dispatch
   *   `artie:dog-weight-changed` — that would recurse back into persist + notify + reload.
   */
  reloadDogsFromLocalStorage(skipDogWeightQuizSync = false) {
    const savedDogs = localStorage.getItem('artie-dogs');
    if (!savedDogs) return;
    try {
      const parsed = JSON.parse(savedDogs);
      if (!Array.isArray(parsed)) return;
      this.dogs = normalizeStoredDogs(parsed);
      if (parsed.length !== this.dogs.length) {
        localStorage.setItem('artie-dogs', JSON.stringify(this.dogs));
        if (!this.dogs.length) localStorage.removeItem('artie_active_pack_dog_id');
      }
      this.activeIndex = activePackIndexForDogs(this.dogs);
      if (this.activeIndex < 0 || this.activeIndex >= this.dogs.length) this.activeIndex = 0;
      try {
        localStorage.setItem('artie-dogs-active', String(this.activeIndex));
      } catch (eSyncIdx) {}
      this.ensureValidDogWeights();
      this.render();
      const active = this.dogs[this.activeIndex];
      if (active && active.id != null) {
        localStorage.setItem('artie_active_pack_dog_id', String(active.id));
      }
      this.updateDynamicText();
      document.dispatchEvent(new CustomEvent('artie:active-dog-changed', { bubbles: true }));
      if (!skipDogWeightQuizSync && active && active.id != null) {
        this.dispatchDogWeightQuizSync(active.id, active.weight, true);
      }
    } catch (e) {
      /* ignore */
    }
  }

  initDefaultDogs() {
    this.dogs = [];
    this.activeIndex = 0;
    this.saveState();
  }

  /** Returns true if any dog weight was coerced to DEFAULT_DOG_WEIGHT. */
  ensureValidDogWeights() {
    let changed = false;
    for (const d of this.dogs) {
      if (!d) continue;
      const w = normalizeDogWeight(d.weight);
      if (d.weight !== w) {
        d.weight = w;
        changed = true;
      }
    }
    return changed;
  }

  /**
   * Notifies artie-calc to recompute portion / variant and refresh quiz localStorage for the active dog.
   * @param {boolean} [immediate] - skip debounce (e.g. right after adding a dog)
   * @param {{ activeDogSwitch?: boolean }} [opts] - pill select only: refresh meal plan without overwriting stored recipe/portion
   */
  dispatchDogWeightQuizSync(packDogId, weight, immediate = false, opts = {}) {
    const w = normalizeDogWeight(weight);
    document.dispatchEvent(
      new CustomEvent('artie:dog-weight-changed', {
        bubbles: true,
        detail: {
          packDogId,
          weightRaw: String(w),
          weightNum: w,
          immediate: !!immediate,
          activeDogSwitch: !!opts.activeDogSwitch
        }
      })
    );
  }

  saveState() {
    localStorage.setItem('artie-dogs', JSON.stringify(this.dogs));
    localStorage.setItem('artie-dogs-active', this.activeIndex.toString());
    const active = this.dogs[this.activeIndex];
    if (active && active.id != null) {
      localStorage.setItem('artie_active_pack_dog_id', String(active.id));
    } else {
      localStorage.removeItem('artie_active_pack_dog_id');
    }
    document.dispatchEvent(new CustomEvent('artie:active-dog-changed', { bubbles: true }));
  }

  addDog() {
    const newDog = {
      id: newPackDogId(this.dogs),
      name: '',
      weight: DEFAULT_DOG_WEIGHT,
      userCreated: true
    };
    this.dogs.push(newDog);
    this.activeIndex = this.dogs.length - 1;
    this.ensureValidDogWeights();
    this.saveState();
    this.render();
    this.dispatchDogWeightQuizSync(newDog.id, newDog.weight, true);

    requestAnimationFrame(() => {
      const rows = this.querySelectorAll('.artie-pack-builder__dog-row');
      const lastRow = rows[rows.length - 1];
      if (lastRow) {
        const input = lastRow.querySelector('.artie-pack-builder__input-name');
        input?.focus();
      }
    });
  }

  removeDog(event) {
    event.stopPropagation();
    const row = event.target.closest('.artie-pack-builder__dog-row');
    const idRaw = row && row.dataset ? row.dataset.id : '';
    
    if (this.dogs.length <= 1) return;

    const indexToRemove = this.dogs.findIndex((d) => d != null && String(d.id) === String(idRaw));
    if (indexToRemove < 0) return;
    const id = this.dogs[indexToRemove].id;
    const wasActive = this.activeIndex === indexToRemove;
    this.dogs.splice(indexToRemove, 1);
    
    if (this.activeIndex >= this.dogs.length) {
      this.activeIndex = this.dogs.length - 1;
    }
    
    this.saveState();
    this.render();
    document.dispatchEvent(new CustomEvent('artie:dog-removed', {
      bubbles: true,
      detail: { packDogId: id, wasActive }
    }));
  }

  selectDog(event) {
    const row = event.target.closest('.artie-pack-builder__dog-row');
    const idRaw = row && row.dataset ? row.dataset.id : '';
    const newIndex = this.dogs.findIndex((d) => d != null && String(d.id) === String(idRaw));

    if (newIndex === this.activeIndex) return;
    if (newIndex < 0) return;

    this.activeIndex = newIndex;
    this.saveState();
    
    this.querySelectorAll('.artie-pack-builder__dog-row').forEach((r, idx) => {
        r.dataset.active = (idx === this.activeIndex);
    });
    
    this.updateDynamicText();

    const active = this.dogs[this.activeIndex];
    if (active && active.id != null) {
      this.dispatchDogWeightQuizSync(active.id, active.weight, true, { activeDogSwitch: true });
    }
  }

  /**
   * Name field: activate this dog when the shopper clicks into the input.
   * The input used `stopEvent` only, so delegation never reached the pill’s `selectDog` handler.
   */
  onMainDogNameClick(event) {
    this.selectDog(event);
    const input = event.target;
    if (input instanceof HTMLInputElement) {
      requestAnimationFrame(() => input.select());
    }
  }

  /**
   * Weight field: same as name — activate this row when clicking to edit weight.
   * Also select the whole value so typing replaces it — precisely placing the
   * caret on a single digit is fiddly on mobile.
   */
  onWeightInputClick(event) {
    this.selectDog(event);
    const input = event.target;
    if (input instanceof HTMLInputElement) {
      requestAnimationFrame(() => input.select());
    }
  }

  updateName(event) {
    const row = event.target.closest('.artie-pack-builder__dog-row');
    const idRaw = row && row.dataset ? row.dataset.id : '';
    const dog = this.dogs.find((d) => d != null && String(d.id) === String(idRaw));
    if (dog) {
      const previousName = dog.name;
      dog.name = String(event.target.value ?? '').trim();
      // Sync `artie_pets_store` before `saveState` → `artie:active-dog-changed` so variant-picker
      // resync reads an up-to-date store (avoids duplicate pets-store writes / fights).
      if (previousName !== dog.name) {
        document.dispatchEvent(
          new CustomEvent('artie:dog-name-changed', {
            bubbles: true,
            detail: { packDogId: dog.id, previousName, newName: dog.name }
          })
        );
      }
      this.saveState();
      this.updateDynamicText();
    }
    for (const el of this.querySelectorAll('.js-doges-name-empty, .js-doges-weight-empty')) {
      if (el instanceof HTMLElement) el.setAttribute('hidden', '');
    }
  }

  updateWeight(event) {
    const row = event.target.closest('.artie-pack-builder__dog-row');
    const idRaw = row && row.dataset ? row.dataset.id : '';
    const dog = this.dogs.find((d) => d != null && String(d.id) === String(idRaw));
    if (dog) {
      dog.weight = event.target.value;
      this.saveState();
      const raw = String(dog.weight ?? '').trim();
      const num = parseInt(raw, 10);
      document.dispatchEvent(
        new CustomEvent('artie:dog-weight-changed', {
          bubbles: true,
          detail: {
            packDogId: dog.id,
            weightRaw: raw,
            weightNum: !Number.isNaN(num) && isPackDogWeightInRange(num) ? num : null
          }
        })
      );
    }
    for (const el of this.querySelectorAll('.js-doges-name-empty, .js-doges-weight-empty')) {
      if (el instanceof HTMLElement) el.setAttribute('hidden', '');
    }
  }

  /** Normalize weight and run a full meal-plan sync after the shopper finishes typing. */
  finalizeWeight(event) {
    const row = event.target.closest('.artie-pack-builder__dog-row');
    const idRaw = row && row.dataset ? row.dataset.id : '';
    const dog = this.dogs.find((d) => d != null && String(d.id) === String(idRaw));
    if (!dog || !(event.target instanceof HTMLInputElement)) return;
    const w = clampPackDogWeight(event.target.value);
    dog.weight = w;
    event.target.value = String(w);
    this.saveState();
    this.dispatchDogWeightQuizSync(dog.id, dog.weight, true);
    for (const el of this.querySelectorAll('.js-doges-name-empty, .js-doges-weight-empty')) {
      if (el instanceof HTMLElement) el.setAttribute('hidden', '');
    }
  }

  updateDynamicText() {
    const activeDog = this.dogs[this.activeIndex] || this.dogs[0] || {};
    const rawActiveName = activeDog.name?.trim();
    const activeName = rawActiveName || 'your dog';
    const isFallback = !rawActiveName;
    
    // 1. Update PERSONALIZED FOR [NAME]
    if (this.refs.personalizedText) {
      const template = this.getAttribute('data-personalized-text') || '';
      this.refs.personalizedText.textContent = template.replace(/\[name\]/gi, activeName.toUpperCase());
    }

    // 2. Update summary list (Matching Video Style)
    if (this.refs.configuringText) {
      if (this.dogs.length === 1 && isFallback) {
         this.refs.configuringText.style.display = 'none';
      } else {
         this.refs.configuringText.style.display = '';
         
         const others = this.dogs
           .filter((_, idx) => idx !== this.activeIndex)
           .map(dog => {
              const name = dog.name?.trim() || 'your dog';
              return `<strong>${name}'s</strong>`;
           });

         const firstPart = `<strong>${activeName}'s</strong> meals`;
         const othersPart = others.length > 0 ? ` · ${others.join(' · ')} plan queued` : '';
         const sharedPart = ` · one Feeding System shared.`;
         
         this.refs.configuringText.innerHTML = `Configuring ${firstPart}${othersPart}${sharedPart}`;
      }
    }

    this.updateGlobalPlaceholders(activeName);
  }

  initializeGlobalPlaceholders() {
    this.placeholders = [];
    const container = this.closest('.product-details') || this.parentElement;
    if (!container) return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.parentElement || node.parentElement.closest('pack-builder')) continue;
      if (node.textContent.includes('[name]') || node.textContent.includes('[NAME]')) {
        this.placeholders.push({
          node: node,
          original: node.textContent
        });
      }
    }
  }

  updateGlobalPlaceholders(name) {
    if (!this.placeholders) return;

    this.placeholders.forEach((item) => {
      let text = item.original;
      text = text.replace(/\[name\]/g, name);
      text = text.replace(/\[NAME\]/g, name.toUpperCase());
      item.node.textContent = text;
    });
  }

  render() {
    const container = this.refs.dogsList;
    const templateElement = this.querySelector('#dog-row-template');
    if (!container || !templateElement) return;

    // Remove only dog rows, keep the add button
    const existingRows = container.querySelectorAll('.artie-pack-builder__dog-row');
    existingRows.forEach(row => row.remove());

    const template = templateElement.content;
    const addButton = this.refs.addButton;
    
    this.dogs.forEach((dog, index) => {
      const clone = template.cloneNode(true);
      const row = clone.querySelector('.artie-pack-builder__dog-row');
      row.dataset.id = String(dog.id);
      row.dataset.active = index === this.activeIndex;
      
      const nameInput = clone.querySelector('.artie-pack-builder__input-name');
      nameInput.value = dog.name;
      
      const weightInput = clone.querySelector('.artie-pack-builder__input-weight');
      weightInput.value = dog.weight;

      const removeBtn = clone.querySelector('.artie-pack-builder__remove-button');
      if (this.dogs.length === 1) {
        removeBtn.style.display = 'none';
      }

      if (addButton) {
        container.insertBefore(clone, addButton);
      } else {
        container.appendChild(clone);
      }
    });

    this.updateDynamicText();
  }
}

customElements.define('pack-builder', PackBuilder);
